import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import CompanyForm from './components/CompanyForm';
import Icon from '../../components/AppIcon';
import { Badge, Button, Note, usePageTitle } from '../../components/app2';
import { authService } from '../../utils/authService';
import { strapiService } from '../../utils/api';
import AlertService from '../../utils/alertService';
import billingService from '../../utils/billingService';

const CompanyFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  
  // Get company data from navigation state (for editing)
  const selectedCompany = location?.state?.company || null;
  const mode = location?.state?.mode || 'create';

  // El header del shell distingue alta de edición (y nombra la empresa).
  usePageTitle({
    kicker: 'Plataforma',
    title: mode === 'edit'
      ? `Editar ${selectedCompany?.name || selectedCompany?.businessName || 'empresa'}`
      : 'Alta de empresa',
  });

  // UPDATED: Handle company creation with SweetAlert2 integration
  const handleSaveCompany = async (companyData) => {
    setIsLoading(true);
    
    try {
      if (mode === 'create') {
        // CRITICAL FIX: Ensure ALL required fields are present and properly mapped
        const apiPayload = {
          // Required fields according to API documentation - FIXED MAPPING
          name: companyData?.businessName || companyData?.name, // businessName -> name
          rfc: companyData?.rfc?.toUpperCase(),
          contact_email: companyData?.contactEmail || companyData?.contact_email, // contactEmail -> contact_email
          
          // Optional fields
          address: companyData?.address || null,
          contact_phone: companyData?.contactPhone || companyData?.phone || null, // contactPhone -> contact_phone
          logo: companyData?.logo || null,
        };

        // CRITICAL: Validate required fields before sending request with SweetAlert2
        if (!apiPayload?.name) {
          await AlertService?.error('Campo requerido', 'El nombre de la empresa es requerido');
          return;
        }
        if (!apiPayload?.rfc || apiPayload?.rfc?.length < 12 || apiPayload?.rfc?.length > 13) {
          await AlertService?.error('RFC inválido', 'RFC debe tener entre 12 y 13 caracteres');
          return;
        }
        if (!apiPayload?.contact_email) {
          await AlertService?.error('Email requerido', 'El email de contacto es requerido para crear el usuario administrador');
          return;
        }

        console.log('🚀 Sending company creation request with validated payload:', apiPayload);
        
        // Show loading
        AlertService?.loading('Creando empresa...', 'Por favor espere mientras se procesa la creación de la empresa y el usuario administrador.');

        // Create new company using API
        const result = await strapiService?.companies?.createCompany(apiPayload);

        // Handle successful creation - UPDATED: Fixed response structure mapping according to API documentation
        console.log('Company created successfully:', result);

        // Cliente de despacho (iguala): aplicar el plan interno sin cobro sobre
        // la empresa recién creada. Si falla, la empresa YA existe — avisar para
        // que el admin lo marque desde edición, sin romper el flujo de alta.
        if (companyData?.is_despacho === true) {
          const newCompanyId = result?.data?.id;
          if (newCompanyId) {
            try {
              await billingService?.admin?.setDespacho(newCompanyId, true);
              console.log('✅ Despacho plan applied to new company:', newCompanyId);
            } catch (despachoError) {
              console.error('❌ Error applying despacho plan:', despachoError);
              await AlertService?.warning(
                'Empresa creada, plan de despacho pendiente',
                'La empresa se creó pero no se pudo aplicar el plan de despacho. Márcala como cliente de despacho desde la edición de la empresa.'
              );
            }
          } else {
            console.error('❌ No company id in create response — cannot apply despacho plan');
          }
        }
        
        // Show success message based on user reactivation
        const successMessage = result?.wasReactivated 
          ? 'Empresa creada y usuario administrador reactivado exitosamente'
          : 'Empresa y usuario administrador creados exitosamente';
          
        const detailMessage = result?.emailSent
          ? 'Se ha enviado un email con las credenciales al administrador.'
          : 'No se pudo enviar el email de credenciales.';
        
        // FIXED: Correct path to response data according to API documentation
        // Response structure: { data: {...}, adminUser: {...}, message: "..." }
        const companyName = result?.data?.name || companyData?.businessName;
        const adminEmail = result?.adminUser?.email || companyData?.contactEmail;
        const apiMessage = result?.message || '';

        // Close loading
        AlertService?.close();
        
        // Show detailed success feedback with SweetAlert2
        await AlertService?.success(
          successMessage,
          `${detailMessage}\n\nEmpresa: ${companyName}\nAdministrador: ${adminEmail}\n\nEstado: ${result?.adminUser?.must_change_password ? 'Debe cambiar contraseña en primer acceso' : 'Usuario configurado'}`,
          { 
            autoClose: false,
            html: `
              <div class="text-left">
                <p class="mb-3">${detailMessage}</p>
                <div class="bg-gray-50 p-3 rounded text-sm space-y-1">
                  <p><strong>Empresa:</strong> ${companyName}</p>
                  <p><strong>Administrador:</strong> ${adminEmail}</p>
                  <p><strong>Estado:</strong> ${result?.adminUser?.must_change_password ? 'Debe cambiar contraseña en primer acceso' : 'Usuario configurado'}</p>
                </div>
              </div>
            `
          }
        );
        
        // Navigate back to company management
        navigate('/app/company-management', { 
          state: { 
            successMessage,
            createdCompany: result?.data?.data?.company
          } 
        });
        
      } else {
        // IMPLEMENTED: Update existing company using PUT /api/companies/:id
        console.log('🔄 Updating company:', companyData);
        
        if (!selectedCompany?.id) {
          await AlertService?.error('Error', 'No se pudo identificar la empresa a actualizar');
          return;
        }

        // Prepare update payload according to API documentation
        const updatePayload = {};
        
        // Add only changed fields
        if (companyData?.name?.trim()) updatePayload.name = companyData?.name?.trim();
        if (companyData?.rfc?.trim()) updatePayload.rfc = companyData?.rfc?.toUpperCase()?.trim();
        if (companyData?.address?.trim()) updatePayload.address = companyData?.address?.trim();
        if (companyData?.contact_email?.trim()) updatePayload.contact_email = companyData?.contact_email?.trim();
        if (companyData?.contact_phone?.trim()) updatePayload.contact_phone = companyData?.contact_phone?.trim();
        if (companyData?.logo !== undefined) updatePayload.logo = companyData?.logo;

        console.log('🚀 Sending company update request with payload:', updatePayload);
        
        AlertService?.loading('Actualizando empresa...', 'Por favor espere mientras se guardan los cambios.');
        
        // Update company using API
        const result = await strapiService?.companies?.updateCompany(selectedCompany?.id, updatePayload);
        
        console.log('Company updated successfully:', result);
        
        AlertService?.close();
        await AlertService?.success(
          'Empresa actualizada', 
          `La información de "${result?.data?.name}" se ha actualizado correctamente.`
        );
        
        navigate('/app/company-management', { 
          state: { 
            successMessage: 'Empresa actualizada correctamente',
            updatedCompany: result?.data
          } 
        });
      }
      
    } catch (error) {
      console.error('Error saving company:', error);
      
      // Close loading
      AlertService?.close();
      
      // Handle specific API errors according to documentation with SweetAlert2
      if (error?.response?.data?.error) {
        const apiError = error?.response?.data?.error;
        
        switch (apiError?.name) {
          case 'BadRequestError':
            if (apiError?.message?.includes('RFC')) {
              await AlertService?.error('RFC duplicado', 'El RFC ya está registrado o es inválido');
            } else if (apiError?.message?.includes('email')) {
              await AlertService?.error('Email duplicado', 'El email ya está registrado con un usuario activo');
            } else if (apiError?.message?.includes('Missing required fields')) {
              await AlertService?.error('Campos faltantes', 'Faltan campos requeridos (nombre, RFC, email de contacto)');
            } else {
              await AlertService?.error('Error de datos', apiError?.message);
            }
            break;
            
          case 'ForbiddenError': await AlertService?.error('Sin permisos', mode === 'create' ?'No tiene permisos para realizar esta acción. Solo Super Admins pueden crear empresas.' :'Company Admin intentando actualizar otra empresa');
            break;

          case 'NotFoundError': await AlertService?.error('Empresa no encontrada', 'La empresa que intenta actualizar no existe o fue eliminada');
            break;
            
          default:
            await AlertService?.error(`Error al ${mode === 'create' ? 'crear' : 'actualizar'} empresa`, apiError?.message || 'Error desconocido');
        }
      } else if (error?.response?.status) {
        // Handle HTTP status codes according to API documentation
        switch (error?.response?.status) {
          case 400:
            await AlertService?.error('RFC duplicado o datos inválidos', 'Verifique que el RFC no esté registrado y que todos los datos sean correctos');
            break;
          case 403:
            await AlertService?.error('Sin permisos', mode === 'create' ?'Solo Super Admins pueden crear empresas' :'Company Admin intentando actualizar otra empresa');
            break;
          case 404:
            await AlertService?.error('Empresa no encontrada', 'La empresa que intenta actualizar no existe');
            break;
          default:
            await AlertService?.error(
              'Error de conexión', 
              'Error al guardar la empresa. Verifique su conexión e intente nuevamente.'
            );
        }
      } else {
        await AlertService?.error(
          'Error de conexión', 
          'Error al guardar la empresa. Verifique su conexión e intente nuevamente.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    navigate('/app/company-management');
  };

  const handleFielUploadComplete = (fielData) => {
    console.log('FIEL upload completed:', fielData);
  };

  const handleLogout = () => {
    try {
      authService?.logout();
    } catch (error) {
      console.error('Error during logout:', error);
      navigate('/login', { replace: true });
    }
  };

  // Get current user data and determine permissions
  const currentUser = authService?.getCurrentUser();
  const userType = currentUser?.user_type || 'company_admin';

  // Check if user can create companies (only Super Admins according to API docs)
  const canCreateCompany = userType === 'super_admin';

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{selectedCompany ? 'Editar Empresa' : 'Nueva Empresa'} - PaySlip</title>
        <meta name="description" content={selectedCompany 
          ? 'Editar información de empresa y configuración SAT' 
          : 'Crear nueva empresa con creación automática de usuario administrador'} />
      </Helmet>
      <div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
          {/* Permission Check */}
          {mode === 'create' && !canCreateCompany && (
            <div className="mb-4 sm:mb-6 bg-error/10 border border-error/20 rounded-lg p-3 sm:p-4">
              <div className="flex items-center space-x-3">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <div>
                  <p className="text-sm font-medium text-error">Sin Permisos</p>
                  <p className="text-xs text-error/80 mt-1">Solo los Super Administradores pueden crear empresas.</p>
                </div>
              </div>
            </div>
          )}

          {/* Encabezado. El título va al header del shell (usePageTitle más
              arriba); aquí quedan el regreso a la lista, la identidad de la
              empresa que se edita y la nota de lo que hará el alta.

              Se retiró el bloque de diagnóstico que se pintaba a la derecha
              ("Modo Creación · Usuario: … · Super Admin · ✅ Permisos OK"): era
              información de depuración a la vista del cliente. Si faltan
              permisos, la pantalla ya lo dice con su propio aviso. */}
          <div className="mb-4 sm:mb-6">
            <Button
              variant="ghost"
              size="sm"
              icon="arrow_back"
              onClick={() => navigate('/app/company-management')}
              style={{ marginBottom: 12, paddingLeft: 0 }}
            >
              Volver a empresas
            </Button>

            <p className="a2-t-muted" style={{ font: "400 .95rem/1.5 'Hanken Grotesk'", margin: 0, maxWidth: 620 }}>
              {selectedCompany
                ? `Edita la información de ${selectedCompany?.name || selectedCompany?.businessName} y configura su integración con el SAT.`
                : 'Completa la información para crear una nueva empresa. Se creará automáticamente su usuario administrador.'}
            </p>

            {selectedCompany && (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginTop: 12 }}>
                <Badge
                  tone={selectedCompany?.state === 'activa' ? 'ok'
                    : selectedCompany?.state === 'inactiva' ? 'neutral' : 'warn'}
                  icon={selectedCompany?.state === 'activa' ? 'check_circle'
                    : selectedCompany?.state === 'inactiva' ? 'cancel' : 'pause_circle'}
                >
                  {selectedCompany?.state === 'activa' ? 'Activa'
                    : selectedCompany?.state === 'inactiva' ? 'Inactiva' : 'Suspendida'}
                </Badge>
                <span className="a2-mono a2-t-muted" style={{ fontSize: '.82rem' }}>{selectedCompany?.rfc}</span>
                <span className="a2-t-muted" style={{ font: "500 .82rem 'Hanken Grotesk'" }}>
                  {selectedCompany?.employeeCount || 0} empleados
                </span>
              </div>
            )}

            {mode === 'create' && (
              <Note tone="info" style={{ marginTop: 16, alignItems: 'flex-start' }}>
                <span>
                  <strong style={{ color: 'var(--heading)' }}>Qué pasa al guardar:</strong> se valida que el
                  RFC no esté registrado (12-13 caracteres) y que el correo no tenga un usuario activo, se
                  crea el usuario administrador de la empresa y se le envía un correo con sus credenciales
                  temporales. Si el correo existía inactivo, se reactiva.
                </span>
              </Note>
            )}
          </div>

          {/* Company Form */}
          {(mode === 'edit' || canCreateCompany) && (
            <div className="space-y-6">
              <CompanyForm
                selectedCompany={selectedCompany}
                onSave={handleSaveCompany}
                onCancel={handleCancelEdit}
                isLoading={isLoading}
                onFielUploadComplete={handleFielUploadComplete}
                mode={mode}
                userType={userType}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyFormPage;